import {
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Thermometer,
  Hash,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { DeliveryLine } from '../../types';
import { QUALITY_STATUS_COLORS } from '../../types';

function getLineStatusBadge(line: DeliveryLine) {
  const received = line.receivedQuantity ?? 0;
  const ordered = line.orderedQuantity;

  if (line.status === 'rejected') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
        <XCircle className="h-3 w-3" /> Rejected
      </span>
    );
  }

  if (received >= ordered) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
        <CheckCircle className="h-3 w-3" /> Complete
      </span>
    );
  }

  if (received > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800">
        <AlertCircle className="h-3 w-3" /> Partial
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
      <Clock className="h-3 w-3" /> Pending
    </span>
  );
}

export function LinesTable({ lines }: { readonly lines: DeliveryLine[] }) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Item
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              SKU
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              Ordered
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              Received
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Quality
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Status
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {lines.map((line) => (
            <tr key={line.lineId} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">{line.itemName}</div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {line.lotNumber && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <Hash className="h-3 w-3" /> {line.lotNumber}
                    </span>
                  )}
                  {line.temperature !== undefined && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <Thermometer className="h-3 w-3" /> {line.temperature}°C
                    </span>
                  )}
                  {line.expiryDate && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <Calendar className="h-3 w-3" /> Exp:{' '}
                      {new Date(line.expiryDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">{line.sku}</td>
              <td className="px-4 py-3 text-right text-sm text-gray-900">
                {line.orderedQuantity} {line.unit}
              </td>
              <td className="px-4 py-3 text-right text-sm text-gray-900">
                {line.receivedQuantity === undefined
                  ? '—'
                  : `${line.receivedQuantity} ${line.unit}`}
              </td>
              <td className="px-4 py-3">
                {line.qualityStatus ? (
                  <span
                    className={cn(
                      'rounded-full px-2 py-1 text-xs font-medium',
                      QUALITY_STATUS_COLORS[line.qualityStatus]
                    )}
                  >
                    {line.qualityStatus.charAt(0).toUpperCase() +
                      line.qualityStatus.slice(1)}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-4 py-3">{getLineStatusBadge(line)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
